import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../../api/apiServices";

const SERVICE_OPTIONS = ["all", "airtime", "data", "electricity", "tv"];
const THEME_OPTIONS   = ["emerald","blue","purple","orange","rose","amber"];

function fmt(n) { return Number(n || 0).toLocaleString("en-NG"); }

function Badge({ label, color = "slate" }) {
  const map = { emerald: "bg-emerald-100 text-emerald-700", blue: "bg-blue-100 text-blue-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-500", slate: "bg-slate-100 text-slate-500", purple: "bg-purple-100 text-purple-700" };
  return <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${map[color] || map.slate}`}>{label}</span>;
}

const EMPTY_FORM = {
  code: "", description: "", type: "flat", value: "", maxDiscount: "",
  services: [], minAmount: "", distribution: "public", assignedUsers: "",
  totalLimit: "", perUserLimit: "1", startsAt: "", expiresAt: "",
};

function CouponModal({ coupon, onClose, onSave }) {
  const isNew = !coupon?._id;
  const [form, setForm] = useState(coupon ? {
    ...EMPTY_FORM,
    ...coupon,
    services:      coupon.services?.join(",") || "",
    assignedUsers: coupon.assignedUsers?.join(",") || "",
    startsAt:  coupon.startsAt  ? new Date(coupon.startsAt).toISOString().slice(0, 16)  : "",
    expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().slice(0, 16) : "",
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.code || !form.value) { setError("Code and value required"); return; }
    setSaving(true); setError("");
    try {
      await onSave({
        ...form,
        value:       Number(form.value),
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
        minAmount:   Number(form.minAmount) || 0,
        totalLimit:  form.totalLimit  ? Number(form.totalLimit)  : null,
        perUserLimit: Number(form.perUserLimit) || 1,
        services:    form.services ? form.services.split(",").map(s => s.trim()).filter(Boolean) : [],
        assignedUsers: form.assignedUsers ? form.assignedUsers.split(",").map(s => s.trim()).filter(Boolean) : [],
        startsAt:  form.startsAt  || null,
        expiresAt: form.expiresAt || null,
      });
      onClose();
    } catch (err) { setError(err?.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{isNew ? "Create Coupon" : `Edit: ${coupon.code}`}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Code</label>
              <input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())}
                placeholder="SAVE30" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white font-mono uppercase placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Type</label>
              <select value={form.type} onChange={e => set("type", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none">
                <option value="flat">Flat (₦)</option>
                <option value="percent">Percent (%)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">
                Value ({form.type === "percent" ? "%" : "₦"})
              </label>
              <input type="number" value={form.value} onChange={e => set("value", e.target.value)}
                placeholder="30" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
            </div>
            {form.type === "percent" && (
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Max Discount (₦)</label>
                <input type="number" value={form.maxDiscount} onChange={e => set("maxDiscount", e.target.value)}
                  placeholder="e.g. 500" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
              </div>
            )}
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Description</label>
            <input value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="15% off on your next electricity bill"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Valid Services</label>
              <div className="flex flex-wrap gap-1.5">
                {SERVICE_OPTIONS.map(s => (
                  <button key={s} onClick={() => {
                    const arr = form.services ? form.services.split(",").map(x => x.trim()).filter(Boolean) : [];
                    const next = arr.includes(s) ? arr.filter(x => x !== s) : [...arr, s];
                    set("services", next.join(","));
                  }} className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all capitalize ${
                    form.services?.includes(s) ? "bg-[#00C896]/20 text-[#00C896] border-[#00C896]/40" : "bg-white/5 text-white/40 border-white/10"
                  }`}>{s}</button>
                ))}
              </div>
              <p className="text-white/20 text-[10px] mt-1">Leave empty = all services</p>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Min Purchase (₦)</label>
              <input type="number" value={form.minAmount} onChange={e => set("minAmount", e.target.value)}
                placeholder="0" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Total Limit</label>
              <input type="number" value={form.totalLimit} onChange={e => set("totalLimit", e.target.value)}
                placeholder="unlimited" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Per User Limit</label>
              <input type="number" value={form.perUserLimit} onChange={e => set("perUserLimit", e.target.value)}
                placeholder="1" className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Distribution</label>
            <div className="flex gap-2">
              {["public","targeted"].map(d => (
                <button key={d} onClick={() => set("distribution", d)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize border transition-all ${
                    form.distribution === d ? "bg-[#00C896]/15 border-[#00C896]/40 text-[#00C896]" : "bg-white/5 border-white/10 text-white/40"
                  }`}>{d}</button>
              ))}
            </div>
          </div>

          {form.distribution === "targeted" && (
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">User IDs (comma separated)</label>
              <textarea value={form.assignedUsers} onChange={e => set("assignedUsers", e.target.value)}
                rows={2} placeholder="userId1, userId2, ..."
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white font-mono placeholder-white/20 focus:outline-none resize-none" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Starts At</label>
              <input type="datetime-local" value={form.startsAt} onChange={e => set("startsAt", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none [color-scheme:dark]" />
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Expires At</label>
              <input type="datetime-local" value={form.expiresAt} onChange={e => set("expiresAt", e.target.value)}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white focus:outline-none [color-scheme:dark]" />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create Coupon" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CouponsView() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCoupon, setEditCoupon] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/mtrade/admin/coupons");
      if (r.data.status) setCoupons(r.data.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCoupons(); }, []);

  const handleSave = async (form) => {
    if (editCoupon._id) {
      const r = await apiClient.patch(`/mtrade/admin/coupons/${editCoupon._id}`, form);
      if (r.data.status) { fetchCoupons(); showToast("Coupon updated"); }
    } else {
      const r = await apiClient.post("/mtrade/admin/coupons", form);
      if (r.data.status) { fetchCoupons(); showToast("Coupon created"); }
    }
  };

  const handleToggle = async (c) => {
    await apiClient.patch(`/mtrade/admin/coupons/${c._id}`, { isActive: !c.isActive });
    setCoupons(prev => prev.map(x => x._id === c._id ? { ...x, isActive: !x.isActive } : x));
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this coupon?")) return;
    await apiClient.delete(`/mtrade/admin/coupons/${id}`);
    setCoupons(prev => prev.filter(c => c._id !== id));
    showToast("Coupon deleted");
  };

  const now = new Date();

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500/90 text-white" : "bg-[#00C896]/90 text-black"
        }`}>
          <i className={toast.type === "error" ? "ri-error-warning-line" : "ri-checkbox-circle-line"} />
          {toast.msg}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Coupons</h2>
          <p className="text-white/40 text-xs mt-0.5">Create and distribute discount coupons to users</p>
        </div>
        <button onClick={() => setEditCoupon({})}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896]/15 border border-[#00C896]/30 rounded-xl text-xs text-[#00C896] font-semibold hover:bg-[#00C896]/25">
          <i className="ri-add-line" /> New Coupon
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total", value: coupons.length, icon: "ri-coupon-3-line" },
          { label: "Active", value: coupons.filter(c => c.isActive).length, icon: "ri-checkbox-circle-line", color: "#00C896" },
          { label: "Used Today", value: "-", icon: "ri-bar-chart-2-line" },
        ].map(s => (
          <div key={s.label} className="bg-[#161616] border border-white/5 rounded-2xl px-4 py-3">
            <i className={`${s.icon} text-lg`} style={{ color: s.color || "rgba(255,255,255,0.2)" }} />
            <p className="text-white font-bold text-xl mt-1">{s.value}</p>
            <p className="text-white/30 text-[10px]">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Coupon list */}
      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(4)].map((_, i) => <div key={i} className="px-4 py-4 h-16 animate-pulse" />)}
          </div>
        ) : coupons.length === 0 ? (
          <div className="py-14 text-center">
            <i className="ri-coupon-3-line text-white/15 text-4xl mb-2 block" />
            <p className="text-white/25 text-sm">No coupons yet</p>
          </div>
        ) : (
          coupons.map(c => {
            const expired = c.expiresAt && new Date(c.expiresAt) < now;
            const exhausted = c.totalLimit && c.usedCount >= c.totalLimit;
            return (
              <div key={c._id} className="flex items-center gap-3 px-4 py-3.5 border-b border-white/4 last:border-0 hover:bg-white/3 transition-colors group">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${c.isActive && !expired && !exhausted ? "bg-[#00C896]/15" : "bg-white/5"}`}>
                  <i className={`ri-coupon-3-line text-base ${c.isActive && !expired && !exhausted ? "text-[#00C896]" : "text-white/25"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-sm font-mono">{c.code}</span>
                    <Badge label={c.type === "percent" ? `${c.value}%` : `₦${fmt(c.value)}`} color="emerald" />
                    {c.services?.map(s => <Badge key={s} label={s} />)}
                    {expired    && <Badge label="Expired"   color="red" />}
                    {exhausted  && <Badge label="Exhausted" color="amber" />}
                    {!c.isActive && <Badge label="Disabled" color="slate" />}
                  </div>
                  <p className="text-white/30 text-xs mt-0.5">
                    {c.usedCount || 0}{c.totalLimit ? `/${c.totalLimit}` : ""} uses
                    {c.minAmount > 0 && ` · min ₦${fmt(c.minAmount)}`}
                    {c.expiresAt && !expired && ` · expires ${new Date(c.expiresAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                  <button onClick={() => setEditCoupon(c)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center">
                    <i className="ri-edit-line text-white/50 text-xs" />
                  </button>
                  <button onClick={() => handleToggle(c)} className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.isActive ? "bg-white/5 hover:bg-red-500/20" : "bg-[#00C896]/10 hover:bg-[#00C896]/20"}`}>
                    <i className={`text-xs ${c.isActive ? "ri-pause-line text-red-400/60" : "ri-play-line text-[#00C896]/60"}`} />
                  </button>
                  <button onClick={() => handleDelete(c._id)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center">
                    <i className="ri-delete-bin-line text-red-400/40 text-xs" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {editCoupon !== null && (
        <CouponModal coupon={editCoupon} onClose={() => setEditCoupon(null)} onSave={handleSave} />
      )}
    </div>
  );
}
